"use client";

/**
 * Home, before its data arrives.
 *
 * It was a centred spinner — which tells you the page is busy and nothing
 * else, and then swaps a small glyph for a full screen of cards in one
 * frame. A skeleton that holds the real layout does two things a spinner
 * cannot: the page does not jump when the data lands, because the boxes are
 * already the right size in the right places, and the shape itself says what
 * is coming.
 *
 * IT MIRRORS `HomeScreen` AND HAS TO KEEP DOING SO. The containers here are
 * the same classes as the real ones — the same grids, the same gaps, the same
 * `aspect-square` on the tiles beside the hero — so that the two stay the
 * same shape by construction rather than by someone remembering. Change a
 * layout there and this is the file that has to move with it; a skeleton that
 * has drifted is worse than none, because the jump it causes is the thing it
 * was added to prevent.
 */

import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { cn } from "@/lib/utils";
import { SECTION_CAP_TRIM } from "./chrome";

/** The pane's own shape, without the surface — a skeleton is not a card. */
const BLOCK = "rounded-nf-ds-card";

export function HomeSkeleton({ gutter }: { gutter?: string }) {
  const t = useTranslations("shell");
  return (
    <div className="flex flex-col gap-nf-ds-card py-nf-ds-4 lg:gap-nf-ds-section lg:py-nf-ds-6">
      {/*
        A plain element with a hidden line, not `<output>` or `role="status"`.

        Both of those carry the `status` role, and `creatorAuthGate.test.ts`
        uses `<output>` as its marker for "the protected page is mounted" —
        the assertion that a cache-isolation window never shows the previous
        account's data. A skeleton that answers that selector would make a
        security test pass for the wrong reason, and loosening the test to
        fit this markup is the wrong way round.

        The hidden line is what a screen reader gets: every block here is
        `aria-hidden`, so without it the page announces nothing at all.
      */}
      <span className="sr-only">{t("loading")}</span>

      <div className={cn("flex flex-col gap-nf-ds-2", gutter)}>
        <Skeleton className="h-[30px] w-[180px]" />
        <Skeleton className="h-[18px] w-[260px]" />
      </div>

      {/* The three summary cards. Three at every count, because the skeleton
          cannot know yet whether a take is waiting — and a row that loses a
          card on load is the jump this exists to avoid less badly than a row
          that gains one. */}
      <div className={cn("grid gap-nf-ds-block lg:grid-cols-3", gutter)}>
        <Skeleton className={cn(BLOCK, "h-[180px]")} />
        <Skeleton className={cn(BLOCK, "h-[180px]")} />
        <Skeleton className={cn(BLOCK, "h-[180px]")} />
      </div>

      <section
        className={cn("flex flex-col gap-nf-ds-item", gutter, SECTION_CAP_TRIM)}
      >
        <Skeleton className="h-[24px] w-[140px]" />
        <div className="grid items-stretch gap-nf-ds-block md:grid-cols-2 lg:grid-cols-4">
          <Skeleton
            className={cn(BLOCK, "h-[258px] lg:aspect-square lg:h-auto")}
          />
          <Skeleton
            className={cn(BLOCK, "h-[258px] lg:aspect-square lg:h-auto")}
          />
          <Skeleton className={cn(BLOCK, "h-[258px] md:col-span-2")} />
          <Skeleton
            className={cn(BLOCK, "h-[200px] md:col-span-2 lg:col-span-4")}
          />
        </div>
      </section>

      <section
        className={cn("flex flex-col gap-nf-ds-item", gutter, SECTION_CAP_TRIM)}
      >
        <Skeleton className="h-[24px] w-[160px]" />
        <Skeleton className="h-[18px] w-[320px] max-w-full" />
        {/* The shelf's own tiles, at the width the row uses, clipped rather
            than wrapped — the real one scrolls sideways. */}
        <div className="flex gap-nf-ds-block overflow-hidden">
          {["a", "b", "c", "d", "e"].map((key) => (
            <Skeleton
              key={key}
              className={cn(
                BLOCK,
                "aspect-[2/3] w-[260px] shrink-0 lg:w-[220px]",
              )}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
